import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export interface OptionContentProps {
  icon?: BenchlingIconName;
  iconClassName?: string;
  iconWrapperClassName?: string;
  avatar?: React.ReactNode;
  avatarClassName?: string;
  contentClassName?: string;
  childrenClassName?: string;
  optionNameClassName?: string;
  metadata?: React.ReactNode;
  children: React.ReactNode;
  hasSubmenu?: boolean;
  className?: string;
  /** Must be string or element; matches ui-library Tooltip `text` (not arbitrary React nodes like numbers). */
  tooltipTitle?: JSX.Element | string | null;
  tooltipPlacement?: FloatingPlacement;
  dataTestAttrs?: DataTestAttrs;
  /**
   * If supplied, `titleAttr` populates the `title` attribute of the `menu-option-children` div tag. The
   * `title` attribute is rendered as a tooltip by the browser. This is separate from the tooltip configured
   * by the `tooltipTitle` prop. The motivation for this tooltip is to reveal the full text of the menu
   * item when css ellipsization is enabled, which happens in Option / OptionContainer. In this case
   * `titleAttr` should have the same value as `children`, if `children` is a string.
   */
  titleAttr?: string;
}

/**
 * OptionContent is the presentational component rendered in Option. It arranges the icon, text, and metadata.
 */
function OptionContent(props: OptionContentProps): JSX.Element {
  return (
    <Tooltip text={props.tooltipTitle} placement={props.tooltipPlacement} disableContentSelection={true}>
      <div
        className={classNames('menu-optionContent', props.className)}
        {...getTestProps(props.dataTestAttrs)}
      >
        {props.icon && (
          <div
            className={classNames('menu-option-iconWrapper', props.iconWrapperClassName)}
            aria-hidden="true"
          >
            <BenchlingIcon
              className={classNames('menu-option-icon', props.iconClassName)}
              icon={props.icon}
            />
          </div>
        )}
        {props.avatar && <div className={props.avatarClassName}>{props.avatar}</div>}
        <div
          className={classNames('menu-option-children', props.childrenClassName)}
          title={props.titleAttr}
          {...getTestProps({component: 'OptionContent', element: 'optionNameAndMetadata'})}
        >
          <div
            className={classNames('menu-option-name', props.optionNameClassName)}
            role="presentation"
            {...getTestProps({component: 'OptionContent', element: 'optionName'})}
          >
            {props.children}
          </div>
          <div
            className="menu-option-metadata"
            {...getTestProps({component: 'OptionContent', element: 'optionMetadata'})}
          >
            {props.metadata}
          </div>
        </div>
        {props.hasSubmenu && (
          <div className="menu-option-chevronWrapper" aria-hidden="true">
            <BenchlingIcon className="menu-option-chevron" icon="chevron" />
          </div>
        )}
      </div>
    </Tooltip>
  );
}

OptionContent.propTypes = {
  // The content of the option.
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
  // `icon` is rendered as <BenchlingIcon icon={icon} /> in the option.
  icon: PropTypes.string,
  iconClassName: PropTypes.string,
  // Rendered under the main text.
  metadata: PropTypes.node,
  hasSubmenu: PropTypes.bool,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any;

export default OptionContent;
